import type { ReactNode } from 'react';
import { ShieldCheck } from 'lucide-react';
import type { UserProfileDto } from '../../../domain/contracts/api.contracts';
import { useTranslation } from '../../../application/hooks/useTranslation';
import { formatDate } from '../../../domain/formatters/date';
import { Badge } from '../ui/Badge';

/** W04 — read-only KYC tiles (PRD §6.1). Null / missing backend values render "Not on record". */
export function KycDetailsCard({ user }: { user: UserProfileDto }) {
  const { t, locale } = useTranslation();
  const notOnRecord = <span className="muted">{t('profile.notOnRecord')}</span>;
  const rows: Array<[string, ReactNode, boolean?]> = [
    [t('profile.nid'), user.nidMasked ? <span className="tabular">{user.nidMasked}</span> : notOnRecord],
    [t('auth.phone'), <span className="tabular">{user.phone}</span>],
    [t('profile.address'), user.address || notOnRecord, true],
    [t('profile.onboardedAt'), user.onboardedAt ? formatDate(user.onboardedAt, locale) : notOnRecord],
    [
      t('profile.kycStatus'),
      user.kycVerified === undefined ? (
        notOnRecord
      ) : (
        <Badge tone={user.kycVerified ? 'success' : 'neutral'}>
          {user.kycVerified ? t('profile.kycVerified') : t('profile.kycNotVerified')}
        </Badge>
      ),
    ],
  ];

  const statusPill =
    user.kycVerified === true ? (
      <span className="profile-status-pill profile-pill-success">
        <ShieldCheck size={12} aria-hidden />
        {t('profile.pillSecured')}
      </span>
    ) : user.kycVerified === false ? (
      <span className="profile-status-pill profile-pill-warning">
        {t('profile.pillPending')}
      </span>
    ) : (
      <span className="profile-status-pill profile-pill-neutral">
        {t('profile.pillNotSet')}
      </span>
    );

  return (
    <section className="profile-card">
      <div className="profile-card-header">
        <div className="profile-card-header-main">
          <div className="profile-card-icon-tile profile-icon-emerald">
            <ShieldCheck size={18} aria-hidden />
          </div>
          <div className="profile-card-title-wrap">
            <h3 className="profile-card-title">{t('profile.kyc')}</h3>
            <p className="profile-card-subtitle">{t('profile.kycSubtitle')}</p>
          </div>
        </div>
        <div className="profile-card-header-badge">{statusPill}</div>
      </div>

      <div className="profile-card-body">
        <dl className="profile-kv-grid kv" data-testid="kyc-card">
          {rows.map(([label, value, fullWidth]) => (
            <div key={label} className={`profile-kv-item${fullWidth ? ' profile-kv-full' : ''}`}>
              <dt className="profile-kv-label">{label}</dt>
              <dd className="profile-kv-value">{value}</dd>
            </div>
          ))}
        </dl>
      </div>
    </section>
  );
}
